/**
 * Urban Customz header — desktop mega navigation + mobile drawer.
 * Relies on trapFocus / removeTrapFocus from global.js when available.
 */
(function () {
  const DESKTOP = '(min-width: 1200px)';

  function focusTrap(container, el) {
    if (typeof window.trapFocus === 'function') window.trapFocus(container, el);
    else if (el) el.focus();
  }
  function focusRelease(el) {
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(el);
    else if (el) el.focus();
  }

  /* ---------------------------------------------------------------------
     Desktop mega navigation
     --------------------------------------------------------------------- */
  class UcHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-wrapper') || this.parentElement;
      this.mq = window.matchMedia(DESKTOP);
      this.items = Array.from(this.querySelectorAll('.uc-nav__item--has-mega'));
      if (!this.items.length) return;

      this.openDelay = 90;
      this.closeDelay = 170;
      this.lastPointerType = 'mouse';

      this.items.forEach((item) => this.bindItem(item));

      this.onDocClick = (e) => {
        if (!this.contains(e.target)) this.closeAll();
      };
      this.onKeydown = (e) => {
        if (e.key === 'Escape' && this.openItem) {
          const toggle = this.openItem.querySelector('.uc-nav__toggle');
          this.closeAll();
          toggle && toggle.focus();
        }
      };
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKeydown);
      this.mq.addEventListener('change', () => this.closeAll());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKeydown);
    }

    bindItem(item) {
      const link = item.querySelector('.uc-nav__link');
      const toggle = item.querySelector('.uc-nav__toggle');
      const panel = item.querySelector('.uc-mega');
      if (!toggle || !panel) return;

      item.addEventListener('pointerenter', (e) => {
        this.lastPointerType = e.pointerType || 'mouse';
        if (e.pointerType === 'touch' || !this.mq.matches) return;
        clearTimeout(item._closeTimer);
        clearTimeout(this._openTimer);
        const delay = this.openItem ? 40 : this.openDelay;
        this._openTimer = setTimeout(() => this.open(item), delay);
      });

      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'touch') return;
        clearTimeout(this._openTimer);
        clearTimeout(item._closeTimer);
        item._closeTimer = setTimeout(() => this.close(item), this.closeDelay);
      });

      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (item.classList.contains('is-open')) {
          this.close(item);
        } else {
          this.open(item);
          const first = panel.querySelector('a, button');
          first && first.focus();
        }
      });

      // On touch devices at desktop width (e.g. iPad landscape) the first tap opens the panel.
      link.addEventListener('click', (e) => {
        if (this.lastPointerType === 'touch' && this.mq.matches && !item.classList.contains('is-open')) {
          e.preventDefault();
          this.open(item);
        }
      });

      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) {
          // Give focus a tick to settle (e.g. moving between elements inside the panel).
          setTimeout(() => {
            if (!item.contains(document.activeElement)) this.close(item);
          }, 10);
        }
      });
    }

    open(item) {
      if (this.openItem && this.openItem !== item) this.close(this.openItem);
      clearTimeout(item._closeTimer);
      item.classList.add('is-open');
      const toggle = item.querySelector('.uc-nav__toggle');
      toggle && toggle.setAttribute('aria-expanded', 'true');
      this.openItem = item;
      this.wrapper.classList.add('uc-mega-open');
      this.wrapper.preventHide = true;
    }

    close(item) {
      if (!item.classList.contains('is-open')) return;
      item.classList.remove('is-open');
      const toggle = item.querySelector('.uc-nav__toggle');
      toggle && toggle.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;
      if (!this.openItem) {
        this.wrapper.classList.remove('uc-mega-open');
        this.wrapper.preventHide = false;
      }
    }

    closeAll() {
      this.items.forEach((item) => this.close(item));
    }
  }
  if (!customElements.get('uc-header')) customElements.define('uc-header', UcHeader);

  /* ---------------------------------------------------------------------
     Mobile drawer
     --------------------------------------------------------------------- */
  class UcDrawer extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.uc-drawer__toggle');
      this.panel = this.querySelector('.uc-drawer__panel');
      this.backdrop = this.querySelector('.uc-drawer__backdrop');
      this.closeBtn = this.querySelector('.uc-drawer__close');
      if (!this.toggle || !this.panel) return;

      this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
      this.closeBtn && this.closeBtn.addEventListener('click', () => this.close());
      this.backdrop && this.backdrop.addEventListener('click', () => this.close());
      this.panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          this.close();
        }
      });

      // Only one top-level accordion open at a time keeps the list scannable.
      this.panel.querySelectorAll('.uc-drawer__list > li > .uc-drawer__details').forEach((d) => {
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          d.closest('.uc-drawer__list')
            .querySelectorAll(':scope > li > .uc-drawer__details[open]')
            .forEach((other) => other !== d && (other.open = false));
        });
      });

      window.matchMedia(DESKTOP).addEventListener('change', (e) => e.matches && this.isOpen && this.close(true));
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      this.panel.hidden = false;
      if (this.backdrop) this.backdrop.hidden = false;
      // Force a frame so the transition runs from the hidden state.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.classList.add('is-open'));
      });
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      focusTrap(this.panel, this.closeBtn || this.panel);
    }

    close(instant) {
      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      focusRelease(this.toggle);
      const finish = () => {
        if (this.isOpen) return;
        this.panel.hidden = true;
        if (this.backdrop) this.backdrop.hidden = true;
      };
      if (instant) finish();
      else setTimeout(finish, 380);
    }
  }
  if (!customElements.get('uc-drawer')) customElements.define('uc-drawer', UcDrawer);

  /* ---------------------------------------------------------------------
     Live chat links (header + drawer): open Tidio when present, otherwise
     follow the fallback href.
     --------------------------------------------------------------------- */
  /* Chat labels read "Contact" (linking to the fallback page) until the Tidio chat has
     actually loaded; then they switch to the chat label. The green dot is shown only
     while Tidio reports its operators as online. */
  const setChatOnline = (online) => {
    document.querySelectorAll('[data-uc-chat-dot]').forEach((d) => { d.hidden = !online; });
    document.querySelectorAll('[data-uc-chat-status]').forEach((d) => { d.textContent = online ? ' (online now)' : ''; });
  };
  const onChatReady = () => {
    document.querySelectorAll('[data-uc-chat-label]').forEach((el) => {
      if (el.dataset.chatLabel) el.textContent = el.dataset.chatLabel;
    });
    try {
      window.tidioChatApi.on('setStatus', (status) => setChatOnline(status === 'online'));
    } catch (err) {
      /* noop */
    }
  };
  if (window.tidioChatApi && typeof window.tidioChatApi.on === 'function') onChatReady();
  else document.addEventListener('tidioChat-ready', onChatReady, { once: true });

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-uc-chat]');
    if (!link || !window.tidioChatApi) return;
    e.preventDefault();
    const drawer = link.closest('uc-drawer');
    drawer && drawer.isOpen && drawer.close();
    try {
      window.tidioChatApi.show();
      window.tidioChatApi.open();
    } catch (err) {
      /* noop */
    }
  });
})();
